import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Shield,
  ShieldCheck,
  Eye,
  Users,
  FileText,
  Settings,
  TrendingUp,
  Database,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Permission {
  category: string;
  permission: string;
  owner: boolean;
  admin: boolean;
  investor: boolean;
  auditor: boolean;
  regulator: boolean;
  observer: boolean;
}

const permissions: Permission[] = [
  // Company Management
  {
    category: 'Company',
    permission: 'View company details',
    owner: true,
    admin: true,
    investor: true,
    auditor: true,
    regulator: true,
    observer: true,
  },
  {
    category: 'Company',
    permission: 'Edit company details',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Company',
    permission: 'Delete company',
    owner: true,
    admin: false,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },

  // Team Management
  {
    category: 'Team',
    permission: 'View team members',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: true,
  },
  {
    category: 'Team',
    permission: 'Invite team members',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Team',
    permission: 'Remove team members',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Team',
    permission: 'Change user roles',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },

  // Cap Table
  {
    category: 'Cap Table',
    permission: 'View cap table',
    owner: true,
    admin: true,
    investor: true,
    auditor: true,
    regulator: true,
    observer: true,
  },
  {
    category: 'Cap Table',
    permission: 'Issue shares',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Cap Table',
    permission: 'Transfer shares',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Cap Table',
    permission: 'Cancel shares',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },

  // Shareholders
  {
    category: 'Shareholders',
    permission: 'View shareholders',
    owner: true,
    admin: true,
    investor: true,
    auditor: true,
    regulator: true,
    observer: true,
  },
  {
    category: 'Shareholders',
    permission: 'Add shareholders',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Shareholders',
    permission: 'Edit shareholders',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },

  // Data Room
  {
    category: 'Data Room',
    permission: 'View documents',
    owner: true,
    admin: true,
    investor: true,
    auditor: true,
    regulator: true,
    observer: true,
  },
  {
    category: 'Data Room',
    permission: 'Upload documents',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Data Room',
    permission: 'Delete documents',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Data Room',
    permission: 'Share documents',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },

  // Compliance
  {
    category: 'Compliance',
    permission: 'View compliance status',
    owner: true,
    admin: true,
    investor: false,
    auditor: true,
    regulator: true,
    observer: true,
  },
  {
    category: 'Compliance',
    permission: 'Manage compliance',
    owner: true,
    admin: true,
    investor: false,
    auditor: true,
    regulator: false,
    observer: false,
  },
  {
    category: 'Compliance',
    permission: 'File with regulators',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },

  // Reporting
  {
    category: 'Reporting',
    permission: 'View reports',
    owner: true,
    admin: true,
    investor: true,
    auditor: true,
    regulator: true,
    observer: true,
  },
  {
    category: 'Reporting',
    permission: 'Generate reports',
    owner: true,
    admin: true,
    investor: false,
    auditor: true,
    regulator: false,
    observer: false,
  },
  {
    category: 'Reporting',
    permission: 'Export data',
    owner: true,
    admin: true,
    investor: false,
    auditor: true,
    regulator: true,
    observer: false,
  },

  // Financial
  {
    category: 'Financial',
    permission: 'View financials',
    owner: true,
    admin: true,
    investor: true,
    auditor: true,
    regulator: true,
    observer: true,
  },
  {
    category: 'Financial',
    permission: 'Update financials',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
  {
    category: 'Financial',
    permission: 'Create funding rounds',
    owner: true,
    admin: true,
    investor: false,
    auditor: false,
    regulator: false,
    observer: false,
  },
];

const roleDescriptions = [
  {
    role: 'owner',
    name: 'Founder',
    icon: Shield,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description:
      'Full control over the company, all data, and team management. Can perform all actions including deleting the company.',
  },
  {
    role: 'admin',
    name: 'Admin',
    icon: ShieldCheck,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description:
      'Can manage daily operations, team members, cap table, documents, and compliance. Cannot delete the company.',
  },
  {
    role: 'investor',
    name: 'Investor',
    icon: TrendingUp,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description:
      'Can view cap table, financial data, reports, and documents shared with them. No editing permissions.',
  },
  {
    role: 'auditor',
    name: 'Auditor',
    icon: FileText,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    description:
      'Can view all company data, compliance status, and generate audit reports. Can manage compliance checks.',
  },
  {
    role: 'regulator',
    name: 'Regulator',
    icon: Database,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    description:
      'Government/regulatory body access. Can view compliance data, reports, and export regulatory filings.',
  },
  {
    role: 'observer',
    name: 'Observer',
    icon: Eye,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    description:
      'Read-only access to company information. Typically time-limited access for advisors or consultants.',
  },
];

export function RolesPermissionsGuide() {
  const PermissionIcon = ({ granted }: { granted: boolean }) =>
    granted ? (
      <CheckCircle2 className="h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="h-5 w-5 text-gray-300" />
    );

  return (
    <div className="space-y-6">
      {/* Role Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Role Descriptions</CardTitle>
          <CardDescription>
            Understanding different access levels and their responsibilities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {roleDescriptions.map((role) => {
              const Icon = role.icon;
              return (
                <div key={role.role} className="flex gap-3 p-4 border rounded-lg">
                  <div className={`p-2 rounded-lg h-fit ${role.bgColor}`}>
                    <Icon className={`h-5 w-5 ${role.color}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{role.name}</h4>
                      <Badge variant="outline" className="text-xs">
                        {role.role}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{role.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Permissions Matrix */}
      <Card>
        <CardHeader>
          <CardTitle>Permissions Matrix</CardTitle>
          <CardDescription>
            Detailed breakdown of what each role can access and modify
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px] sticky left-0 bg-card">Category</TableHead>
                    <TableHead className="w-[250px]">Permission</TableHead>
                    <TableHead className="text-center">Founder</TableHead>
                    <TableHead className="text-center">Admin</TableHead>
                    <TableHead className="text-center">Investor</TableHead>
                    <TableHead className="text-center">Auditor</TableHead>
                    <TableHead className="text-center">Regulator</TableHead>
                    <TableHead className="text-center">Observer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissions.reduce((acc: JSX.Element[], perm, index, array) => {
                    const isFirstInCategory =
                      index === 0 || array[index - 1].category !== perm.category;

                    if (isFirstInCategory) {
                      acc.push(
                        <TableRow key={`cat-${perm.category}`} className="bg-muted/50">
                          <TableCell
                            colSpan={8}
                            className="font-semibold sticky left-0 bg-muted/50"
                          >
                            {perm.category}
                          </TableCell>
                        </TableRow>
                      );
                    }

                    acc.push(
                      <TableRow key={`${perm.category}-${perm.permission}`}>
                        <TableCell className="sticky left-0 bg-card"></TableCell>
                        <TableCell className="text-sm">{perm.permission}</TableCell>
                        <TableCell className="text-center">
                          <PermissionIcon granted={perm.owner} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PermissionIcon granted={perm.admin} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PermissionIcon granted={perm.investor} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PermissionIcon granted={perm.auditor} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PermissionIcon granted={perm.regulator} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PermissionIcon granted={perm.observer} />
                        </TableCell>
                      </TableRow>
                    );

                    return acc;
                  }, [])}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Best Practices */}
      <Card>
        <CardHeader>
          <CardTitle>Best Practices</CardTitle>
          <CardDescription>Recommendations for managing team access and roles</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Principle of Least Privilege</p>
                <p className="text-sm text-muted-foreground">
                  Grant users the minimum level of access needed for their role
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Use Observer Role for Time-Limited Access</p>
                <p className="text-sm text-muted-foreground">
                  For advisors, consultants, or temporary access, use Observer role with expiry
                  dates
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Limit Founder Role</p>
                <p className="text-sm text-muted-foreground">
                  Only assign Founder role to company founders or C-level executives who need full
                  control
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Regular Access Reviews</p>
                <p className="text-sm text-muted-foreground">
                  Periodically review team members and remove access for users who no longer need it
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Use Appropriate Roles for External Parties</p>
                <p className="text-sm text-muted-foreground">
                  Investors should have Investor role, auditors should have Auditor role - don't use
                  Admin for external parties
                </p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
